
/**
 * Accordion scripts.
 *
 * Handles expanding/collapsing functionality as well as all animations for the accordion.
 */

/** Dependencies -------------------------------------------- */

// External

import { throttle } from 'throttle-debounce'

import gsap from 'gsap'
// import ScrollTrigger from 'gsap/ScrollTrigger'

// gsap.registerPlugin( ScrollTrigger )

// Design system

import { animateGeneralEventName } from '../smooth-scroll'

/** The beef ------------------------------------------------ */

/**
 * Accordion mixin.
 * Provides accordion functionality to single element.
 */
export let mixin = ( { node = null, config = {} } = {} ) => {

    let state = {
        node,
        config,
        items    : null,
        toggles  : null,
        timeline : null
    };

    let module = {

        // Setup

        init() {

            state.items = node.querySelectorAll( state.config.itemSelector )
            state.toggles = node.querySelectorAll( state.config.toggleSelector )

            this.setEventBindings()
            this.setInitialAnimationStates()
        },

        setEventBindings() {

            Array.from( state.toggles ).map(
				( toggle ) => toggle.addEventListener( 'click', this.handleToggleClick.bind( this ) )
			)

           // window.addEventListener( animateGeneralEventName, this.animate.bind( this ) )
        },

        // Main        

        expandItem( item ) {

            const descriptionContainer = item.querySelector( state.config.descriptionContainerSelector )

            item.classList.remove( state.config.isCollapsedClass )
            item.classList.add( state.config.isExpandedClass )

            item.setAttribute( 'aria-expanded', true )

            gsap.to(
                descriptionContainer,
                {
                    height     : 'auto',
                    duration   : state.config.expandDuration,
                    // onUpdate   : throttle( state.config.toggleUpdateThrottle, ScrollTrigger.refresh ),
                    // onComplete : ScrollTrigger.refresh
                }
            )

        },

        collapseItem( item ) {

            const descriptionContainer = item.querySelector( state.config.descriptionContainerSelector )

            item.classList.remove( state.config.isExpandedClass )
            item.classList.add( state.config.isCollapsedClass )

            item.setAttribute( 'aria-expanded', false )

            gsap.to(
                descriptionContainer,
                {
                    height     : 0,
                    duration   : state.config.collapseDuration,
                    // onUpdate   : throttle( state.config.toggleUpdateThrottle, ScrollTrigger.refresh ),
                    // onComplete : ScrollTrigger.refresh
                }
            )            
        },

        setInitialAnimationStates() {

            const itemPaddingTimeline = gsap.timeline( { paused: true } )

            for ( const [ index, item ] of Array.from( state.items ).entries() ) {

                const itemDescriptionContainer = item.querySelectorAll( state.config.descriptionContainerSelector )

                if ( ! item.classList.contains( state.config.isExpandedClass ) ) {
                    gsap.set( itemDescriptionContainer, { height: 0 } )
                }

                const ogPT = window.getComputedStyle( item, null ).getPropertyValue( 'padding-top' )
                const ogPB = window.getComputedStyle( item, null ).getPropertyValue( 'padding-bottom' )
                const offset = index ? `<+${state.config.inStagger / state.items.length}` : null

                gsap.set( item, { paddingTop: 0, paddingBottom: 0 } )
                itemPaddingTimeline.to( item, { paddingTop: ogPT, paddingBottom: ogPB, duration: state.config.inDuration }, offset )

                if ( item.classList.contains( state.config.isExpandedClass ) ) {
                    itemPaddingTimeline.to( itemDescriptionContainer, { height: 'auto', duration: state.config.inDuration }, offset )
                }
            }

            // if ( ! state.timeline ) {

            //     const descriptionContainers = node.querySelectorAll( state.config.descriptionContainerSelector )
            //     const headers = node.querySelectorAll( state.config.headerSelector )

            //     gsap.set( node, { height: node.offsetHeight, opacity: 0, y: '2rem' } )
            //     gsap.set( headers, { height: 0, opacity: 0 } )
            //     gsap.set( descriptionContainers, { height: 0 } )

            //     state.timeline = gsap.timeline( { paused: true } )
            //         .to( node, { opacity: 1, y: 0, duration: state.config.inDuration } )
            //         .add( itemPaddingTimeline.play(), '<' )
            //         .to( headers, { height: 'auto', opacity: 1, duration: state.config.inDuration, stagger: { amount: state.config.inStagger } }, `<+${state.config.inStagger}` )
            //         .set( headers, { clearProps: 'all' } )
            //         .set( state.items, { clearProps: 'all' } )
            //         .set( node, { clearProps: 'all' } )
            // }

        },

        animate() {

            ScrollTrigger.create({
                animation : state.timeline,
                trigger   : node,
                once      : true
            })
        },

        // Handlers

        handleToggleClick( event ) {
            
            const item = event.target.closest( state.config.itemSelector )

            if ( item.getAttribute( 'aria-expanded' ) === 'false' ) {
                this.expandItem( item )
                
            }
            else {
                this.collapseItem( item )
            }
        },
    };

    return module.init()
};

/**
 * Accordion module.
 * Provides an interface to apply accordion functionality to all elements matching a selector.
 */
export default {

    init( { className, config = {} } ) {

        let selected = document.querySelectorAll( `.${className}` )

        if ( ! selected.length ) return

        const defaultConfig = {
            itemSelector                 : `.${className}-item`,
            headerSelector               : `.${className}-item__header`,
            toggleSelector               : `.${className}-item__toggle`,
            descriptionContainerSelector : `.${className}-item__description-container`,
            isExpandedClass              : 'is-expanded',
            isCollapsedClass             : 'is-collapsed',
            expandDuration               : 0.35,
            collapseDuration             : 0.35,
            toggleUpdateThrottle         : 32,
            inDuration                   : 0.4,
            inStagger                    : 0.35
        }

        return Array.from( selected ).map(
            ( node, index ) => mixin( { node, config: { ...defaultConfig, ...config, className } } )
        )
    }
}
